import type { ComponentType, SVGProps } from "react"

type IconProps = { className?: string }

const baseProps: SVGProps<SVGSVGElement> = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
}

/** Diode: triangle + cathode bar with two leads. */
export function DiodeIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 12h5" />
      <path d="M8 6.5v11l8-5.5-8-5.5z" />
      <path d="M16 6.5v11" />
      <path d="M16 12h5" />
    </svg>
  )
}

/** Zener: diode triangle with z-shaped cathode bar. */
export function ZenerIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 12h5" />
      <path d="M8 6.5v11l8-5.5-8-5.5z" />
      <path d="M14 6.5h2v11h2" />
      <path d="M16 12h5" />
    </svg>
  )
}

/** Triac: two diode triangles back-to-back with gate lead. */
export function TriacIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 7h6" />
      <path d="M21 17h-6" />
      <path d="M9 4v8l6-4-6-4z" />
      <path d="M15 20v-8l-6 4 6 4z" />
      <path d="M9 12h6" />
      <path d="M12 12v9" />
    </svg>
  )
}

/** Transistor: circle with base bar + collector/emitter leads. */
export function TransistorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h6" />
      <path d="M9 7v10" />
      <path d="M9 11l8-5" />
      <path d="M17 6v3.5" />
      <path d="M9 13l8 5" />
      <path d="M17 18v-3.5" />
    </svg>
  )
}

/** Resistor: zigzag with two leads (US schematic). */
export function ResistorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M2 12h3l1.5-4 2.5 8 2.5-8 2.5 8 2.5-8 1.5 4h3" />
    </svg>
  )
}

/** Capacitor: two parallel plates with leads. */
export function CapacitorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 12h6" />
      <path d="M9 5v14" />
      <path d="M15 5v14" />
      <path d="M15 12h6" />
    </svg>
  )
}

/** Inductor: series of arcs (4 humps). */
export function InductorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M2 14h2" />
      <path d="M4 14a2 2 0 0 1 4 0" />
      <path d="M8 14a2 2 0 0 1 4 0" />
      <path d="M12 14a2 2 0 0 1 4 0" />
      <path d="M16 14a2 2 0 0 1 4 0" />
      <path d="M20 14h2" />
    </svg>
  )
}

/** Chip: DIP package with side pins. */
export function ChipIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="6" y="4" width="12" height="16" rx="1" />
      <circle cx="9" cy="7.5" r="0.6" fill="currentColor" />
      <path d="M3 8h3M3 12h3M3 16h3" />
      <path d="M21 8h-3M21 12h-3M21 16h-3" />
    </svg>
  )
}

/** LED: diode triangle with two arrows pointing out (light emission). */
export function LedIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 14h5" />
      <path d="M8 8.5v11l8-5.5-8-5.5z" />
      <path d="M16 8.5v11" />
      <path d="M16 14h5" />
      <path d="M14 6l3-3m-3 0h3v3" />
      <path d="M9 5l3-3m-3 0h3v3" />
    </svg>
  )
}

/** LED strip: long thin pcb with regular dots. */
export function LedStripIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="2" y="9" width="20" height="6" rx="1" />
      <circle cx="6" cy="12" r="1" fill="currentColor" />
      <circle cx="10" cy="12" r="1" fill="currentColor" />
      <circle cx="14" cy="12" r="1" fill="currentColor" />
      <circle cx="18" cy="12" r="1" fill="currentColor" />
    </svg>
  )
}

/** LED bulb: classic bulb silhouette with filament. */
export function LedBulbIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M7 11a5 5 0 0 1 10 0c0 2-1 3-2 4v3H9v-3c-1-1-2-2-2-4z" />
      <path d="M10 13l2 2 2-2" />
    </svg>
  )
}

/** LED driver: power-supply box with LED indicator. */
export function LedDriverIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M7 12l2-3 1 6 2-6 1 6 2-3" />
      <circle cx="18" cy="9.5" r="0.8" fill="currentColor" />
    </svg>
  )
}

/** LED fixture: ceiling lamp dome with light rays. */
export function LedFixtureIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M5 10a7 7 0 0 1 14 0v2H5v-2z" />
      <path d="M5 12h14" />
      <path d="M9 16v3" />
      <path d="M12 16v4" />
      <path d="M15 16v3" />
    </svg>
  )
}

/** Crystal: rectangular can with two pins (HC-49 style). */
export function CrystalIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 12h4" />
      <path d="M7 7v10" />
      <path d="M9 5h6v14H9z" />
      <path d="M17 7v10" />
      <path d="M17 12h4" />
    </svg>
  )
}

/** Relay: coil rectangle plus switch contact. */
export function RelayIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="4" y="13" width="10" height="6" rx="0.5" />
      <path d="M6 13v-2" />
      <path d="M12 13v-2" />
      <path d="M9 13v-3" />
      <path d="M14 8h2l4-3" />
      <path d="M9 8h7" />
      <circle cx="9" cy="8" r="0.6" fill="currentColor" />
      <circle cx="16" cy="8" r="0.6" fill="currentColor" />
    </svg>
  )
}

/** Fuse: rounded cylinder with line through. */
export function FuseIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 12h3" />
      <rect x="6" y="9" width="12" height="6" rx="3" />
      <path d="M6 12h12" />
      <path d="M18 12h3" />
    </svg>
  )
}

/** Switch: angled lever between two contacts. */
export function SwitchIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 14h4" />
      <path d="M17 14h4" />
      <circle cx="7" cy="14" r="1.4" />
      <circle cx="17" cy="14" r="1.4" />
      <path d="M8 13l8-7" />
    </svg>
  )
}

/** Button: round push-button with cap. */
export function ButtonIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="12" cy="13" r="7" />
      <circle cx="12" cy="13" r="3" />
      <path d="M12 6V3" />
    </svg>
  )
}

/** Connector: plug shape with prongs. */
export function ConnectorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M5 9h14v5a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9z" />
      <path d="M9 9V5" />
      <path d="M15 9V5" />
      <path d="M12 18v3" />
    </svg>
  )
}

/** Terminal block: connector with screws. */
export function TerminalIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="8" width="18" height="9" rx="1" />
      <circle cx="8" cy="12.5" r="1.5" />
      <circle cx="16" cy="12.5" r="1.5" />
      <path d="M8 11l0 3M16 11l0 3" />
    </svg>
  )
}

/** Wire: curved cable with end caps. */
export function WireIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 7c4 0 4 10 8 10s4-10 8-10" />
      <circle cx="3" cy="7" r="1.2" />
      <circle cx="19" cy="17" r="1.2" />
    </svg>
  )
}

/** Battery: cell with terminals and +/- markers. */
export function BatteryIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="8" width="16" height="9" rx="1" />
      <path d="M19 11v3h2v-3z" />
      <path d="M7 12.5h2M8 11.5v2" />
      <path d="M14 12.5h2" />
    </svg>
  )
}

/** Speaker: cone with sound waves. */
export function SpeakerIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M4 10v5h3l5 4V6L7 10H4z" />
      <path d="M16 9c1.5 1.5 1.5 4.5 0 6" />
      <path d="M19 7c2.5 2.5 2.5 7.5 0 10" />
    </svg>
  )
}

/** Sensor: dot with radar arcs. */
export function SensorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="12" cy="18" r="1.5" fill="currentColor" />
      <path d="M7 18a5 5 0 0 1 10 0" />
      <path d="M4 18a8 8 0 0 1 16 0" />
      <path d="M12 13V8" />
    </svg>
  )
}

/** Fan: 3-blade rotor in housing. */
export function FanIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="1.5" />
      <path d="M12 11c0-3-2-5-5-5 0 3 2 5 5 5z" />
      <path d="M13 12c2.5-1.5 3-4 1.5-6.5C12 7 11.5 9.5 13 12z" />
      <path d="M11.5 13c-2 2-2 5 0 7 2-2 2-5 0-7z" />
    </svg>
  )
}

/** Transformer: two coupled coils with core. */
export function TransformerIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M2 14h2" />
      <path d="M4 14a1.5 1.5 0 0 1 3 0" />
      <path d="M7 14a1.5 1.5 0 0 1 3 0" />
      <path d="M10 14h1" />
      <path d="M11 8v12" />
      <path d="M13 8v12" />
      <path d="M13 14h1" />
      <path d="M14 14a1.5 1.5 0 0 1 3 0" />
      <path d="M17 14a1.5 1.5 0 0 1 3 0" />
      <path d="M20 14h2" />
    </svg>
  )
}

/** Heatsink: fins on top of a base plate. */
export function HeatsinkIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M3 16h18" />
      <path d="M3 19h18" />
      <path d="M5 16V5" />
      <path d="M9 16V5" />
      <path d="M13 16V5" />
      <path d="M17 16V5" />
      <path d="M21 16V5" />
    </svg>
  )
}

/** Board: PCB with traces, IC and pads. */
export function BoardIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="3" width="18" height="18" rx="1.5" />
      <rect x="8" y="9" width="6" height="5" rx="0.5" />
      <circle cx="6" cy="6" r="0.8" />
      <circle cx="18" cy="6" r="0.8" />
      <circle cx="18" cy="18" r="0.8" />
      <path d="M14 11h4M16 14v4" />
    </svg>
  )
}

/** Wireless: antenna with broadcast arcs. */
export function WirelessIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M5 14a10 10 0 0 1 14 0" />
      <path d="M8 17a6 6 0 0 1 8 0" />
      <circle cx="12" cy="20" r="1.2" fill="currentColor" />
      <path d="M12 4v6" />
      <path d="M9 7l3-3 3 3" />
    </svg>
  )
}

/** Motor: round housing with shaft. */
export function MotorIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="11" cy="12" r="7" />
      <circle cx="11" cy="12" r="2" />
      <path d="M18 12h4" />
      <path d="M11 5V3M11 21v-2" />
      <path d="M5 12H3" />
    </svg>
  )
}

/** Multimeter: case with display, dial and probes. */
export function MultimeterIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="4" y="3" width="16" height="14" rx="1.5" />
      <rect x="6" y="5" width="12" height="4" rx="0.5" />
      <circle cx="12" cy="13" r="2" />
      <path d="M12 13v-1.5" />
      <path d="M8 21l-1-3" />
      <path d="M16 21l1-3" />
    </svg>
  )
}

/** Tools: crossed wrench and screwdriver. */
export function ToolsIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M14 7l4-4 3 3-4 4-2-1-7 7-2-2 7-7-1-2 2 2z" />
      <path d="M3 21l5-5" />
      <circle cx="6" cy="18" r="1" />
    </svg>
  )
}

/** Chemistry: erlenmeyer flask. */
export function ChemistryIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M9 3h6" />
      <path d="M10 3v5L5 18a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 18l-5-10V3" />
      <path d="M7.5 14h9" />
    </svg>
  )
}

/** Enclosure: 3D box outline. */
export function EnclosureIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <path d="M4 8l8-4 8 4-8 4-8-4z" />
      <path d="M4 8v9l8 4 8-4V8" />
      <path d="M12 12v9" />
    </svg>
  )
}

/** Power supply: lightning bolt inside box. */
export function PowerIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M13 9l-3 4h2l-1 3 3-4h-2l1-3z" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Knob: dial with indicator notch. */
export function KnobIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4v3" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  )
}

/** Controller: panel with display and buttons. */
export function ControllerIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <rect x="6" y="7" width="12" height="4" rx="0.5" />
      <circle cx="8" cy="15" r="1" />
      <circle cx="12" cy="15" r="1" />
      <circle cx="16" cy="15" r="1" />
      <path d="M8 18h8" />
    </svg>
  )
}

/** Default: dashed grid (catch-all). */
export function DefaultIcon({ className }: IconProps) {
  return (
    <svg {...baseProps} className={className}>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </svg>
  )
}

export type CategoryIcon = ComponentType<IconProps>
